/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useState, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { isEmpty } from 'lodash'
import { type GenericAutocompleteProps } from '../FormAutoComplete'
import api from '../../../utils/api'
import MultiSelectWithCheckBox, { type IRenderTagsType } from '../MultiSelectWithCheckBox'
import { type SxProps, type Theme } from '@mui/system'

interface MultiChipApiAutoCompleteProps<T> extends Omit<GenericAutocompleteProps<T>, 'getOptionLabel' | 'getOptionValue' | 'loading' | 'onChange' | 'options'> {
  selectedItem?: T
  apiEndpoint: string
  labelField: string
  valueField?: string
  WholeValue?: boolean
  control?: any
  onChange?: (arg: T) => void
  placeholder: string
  name: string
  label?: string
  disabled?: boolean
  multiple?: boolean
  required?: boolean
  renderTagsType?: IRenderTagsType
  customPaperStyle?: object
  customInputProps?: object
  fullWidth?: boolean
  sx?: SxProps<Theme>
}

export const MultiChipApiAutoComplete = <T,>({
  selectedItem,
  apiEndpoint,
  labelField,
  valueField: paramValueField,
  WholeValue,
  control,
  onChange,
  placeholder,
  name,
  label,
  disabled,
  multiple,
  required,
  renderTagsType,
  customPaperStyle,
  customInputProps,
  fullWidth,
  sx,
  ...props
}: MultiChipApiAutoCompleteProps<T>): JSX.Element => {
  const valueField = paramValueField ?? 'value'
  const [input, setInput] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)

  // Convert camelCase to snake_case for field names (if necessary)
  const reverseCamelCase = (str: string): string => str.replace(/([A-Z])/g, '_$1').toLowerCase()

  const labelFieldUnderscore = reverseCamelCase(labelField)

  const { data = [], isLoading } = useQuery<T[]>({
    queryKey: [apiEndpoint, input, selectedItem],
    staleTime: 10,
    queryFn: async (): Promise<T[]> => {
      if (selectedItem && isEmpty(input)) {
        return [selectedItem]
      }

      setLoading(true)
      try {
        const result = await api.get(apiEndpoint, {
          params: {
            pageSize: 10,
            filters: input
              ? JSON.stringify({
                field: labelFieldUnderscore,
                op: 'like',
                value: `${input}%`
              })
              : undefined,
            sort: JSON.stringify({
              field: labelFieldUnderscore,
              direction: 'asc'
            })
          }
        })
        return result.data
      } catch (error) {
        console.error('API call failed:', error)
        return []
      } finally {
        setLoading(false)
      }
    },
    enabled: !!apiEndpoint
  })

  return (
    <MultiSelectWithCheckBox
      name={name}
      label={label}
      placeholder={placeholder}
      disabled={disabled}
      required={required}
      renderTagsType={renderTagsType}
      customInputProps={customInputProps}
      customPaperStyle={customPaperStyle}
      fullWidth={fullWidth}
      getOptionLabel={(option: any) => option[labelField] ?? ''}
      getOptionValue={(option: any) => option[valueField] ?? ''}
      options={data}
      loading={loading || isLoading}
      onInputChange={(value: string) => { setInput(value) }}
      sx={sx}
      {...props} />
  )
}
